/**
 * Generator 2b: Server actions for single (singleton) schemas
 * Split into individual files: types.ts, get-{singular}.ts, upsert-{singular}.ts, index.ts
 */

import { isLayoutField } from '@core-engine/schema/constants.js'
import { flattenFields, getManyToManyFields } from '@core-engine/schema/field-helpers/index.js'
import { isUserRelationshipTarget } from '@core-engine/schema/system-relationships.js'
import type { Schema, SchemaField } from '@core-engine/schema/types.js'
import { createGeneratedFile } from '@core-engine/utils/generated-files.js'
import {
  pluralize,
  quotePropertyName,
  singularize,
  toCamelCase,
  toKebabCase,
  toPascalCase
} from '@core-engine/utils/string.js'
import { resolveSchemaActionDir } from '@next-adapter/config/paths.js'
import type { ActionsGeneratorResult } from '@next-adapter/generators/actions/action-helpers.js'
import {
  buildAuthorshipAliasBlock,
  buildAuthorshipDataFields,
  buildAuthorshipJoins,
  buildAuthorshipSelects,
  buildAuthorshipUserType,
  buildExplicitSelect,
  buildReadSelectOptionType,
  buildRelationshipDataTypes,
  buildRelationshipSelect,
  buildSingleRowMapping,
  collectReadSelectFields,
  createAuthorshipFields,
  getInputFieldType,
  getReadFieldType,
  isAuthorshipFieldName,
  type ReadSelectField
} from '@next-adapter/generators/actions/action-helpers.js'
import {
  buildSelectOptionTypes,
  genCreateSelectOptionContent,
  genGetSelectOptionsContent,
  type SelectOptionCtx
} from '@next-adapter/generators/actions/entity-file-contents.js'
import { genMarkdownRendererContent } from '@next-adapter/generators/actions/markdown-renderer.js'

function generateSingleResolveSelectFieldsFunction(
  Singular: string,
  selectFields: ReadSelectField[],
  selectOptionsTableVar: string,
  hasCreatableSelectFields: boolean
): string {
  const staticLabelMaps = selectFields
    .map((readField) => {
      const key = readField.path.join('.')
      const entries = (readField.field.options ?? [])
        .map((option) => `${JSON.stringify(option.value)}: ${JSON.stringify(option.label)}`)
        .join(', ')
      return `  ${JSON.stringify(key)}: { ${entries} }`
    })
    .join(',\n')
  const dynamicRowsBlock = hasCreatableSelectFields
    ? `
  const dynamicRows = await db
    .select({
      fieldName: ${selectOptionsTableVar}.fieldName,
      label: ${selectOptionsTableVar}.label,
      value: ${selectOptionsTableVar}.value
    })
    .from(${selectOptionsTableVar})

  for (const row of dynamicRows) {
    labelMaps[row.fieldName] ??= {}
    labelMaps[row.fieldName][row.value] = row.label
  }
`
    : ''
  const assignments = selectFields
    .map((readField) => {
      const key = readField.path.join('.')
      return `    resolveSelectFieldPath(source, ${JSON.stringify(readField.path)}, labelMaps[${JSON.stringify(key)}] ?? {})`
    })
    .join('\n')

  return `const STATIC_SELECT_OPTION_LABELS = {
${staticLabelMaps}
} satisfies Record<string, Record<string, string>>

function cloneSelectOptionLabelMaps(): Record<string, Record<string, string>> {
  return Object.fromEntries(
    Object.entries(STATIC_SELECT_OPTION_LABELS).map(([fieldName, labels]) => [
      fieldName,
      { ...labels }
    ])
  )
}

function toSelectOptionValues(value: unknown, labels: Record<string, string>): SelectOptionValue[] {
  const values = Array.isArray(value) ? value : value ? [value] : []

  return values
    .filter((item): item is string => typeof item === 'string' && item.length > 0)
    .map((item) => ({
      label: labels[item] ?? item,
      value: item
    }))
}

function resolveSelectFieldPath(
  source: Record<string, unknown>,
  path: readonly string[],
  labels: Record<string, string>
): void {
  const [fieldName, ...rest] = path
  if (!fieldName) return

  if (rest.length === 0) {
    source[fieldName] = toSelectOptionValues(source[fieldName], labels)
    return
  }

  const value = source[fieldName]
  if (Array.isArray(value)) {
    for (const item of value) {
      if (item && typeof item === 'object') {
        resolveSelectFieldPath(item as Record<string, unknown>, rest, labels)
      }
    }
    return
  }

  if (value && typeof value === 'object') {
    resolveSelectFieldPath(value as Record<string, unknown>, rest, labels)
  }
}

async function resolve${Singular}SelectFields(rows: ${Singular}[]): Promise<${Singular}[]> {
  if (rows.length === 0) return rows

  const labelMaps = cloneSelectOptionLabelMaps()
${dynamicRowsBlock}
  for (const row of rows) {
    const source = row as unknown as Record<string, unknown>
${assignments}
  }

  return rows
}`
}

function getSingleM2MContext(schemaName: string, field: SchemaField) {
  const singularSchema = singularize(schemaName)
  const relationship = field.relationship ?? ''
  const relationshipSingular = singularize(relationship)

  return {
    junctionVar: toCamelCase(`${singularSchema}${toPascalCase(relationship)}`),
    schemaIdCol: `${singularSchema}Id`,
    relationIdCol: `${relationshipSingular}Id`,
    relationIdVar: `${relationshipSingular}Id`,
    inputIdsVar: `${field.name}Ids`
  }
}

/**
 * Generate server actions for a single (singleton) schema, split into individual files.
 */
export function generateSingleActions(
  schema: Schema,
  actionsDir: string,
  options: { force?: boolean; nextMajorVersion?: number; outputNamespace?: string } = {}
): ActionsGeneratorResult {
  const singular = singularize(schema.name)
  const plural = pluralize(schema.name)
  const Singular = toPascalCase(singular)
  const tableVar = toCamelCase(schema.name)
  const camelSingular = toCamelCase(singular)
  const kebabSingular = toKebabCase(singular)
  const schemaActionDir = resolveSchemaActionDir(actionsDir, schema.name, options.outputNamespace)

  const dbFields = flattenFields(schema.fields).filter(
    (f) => !(f.type === 'relationship' && f.multiple === true) && !isLayoutField(f.type)
  )
  const m2mFields = getManyToManyFields(schema.fields)
  const hasM2M = m2mFields.length > 0

  const htmlOutputFields = dbFields.filter(
    (f) => (f.type === 'richtext' || f.type === 'markdown') && f.output === 'html'
  )
  const hasHtmlOutput = htmlOutputFields.length > 0
  const creatableSelectFields = dbFields.filter(
    (field) => field.type === 'select' && field.creatable === true
  )
  const hasCreatableSelectFields = creatableSelectFields.length > 0
  const selectFields = collectReadSelectFields(dbFields)
  const hasSelectFields = selectFields.length > 0
  const relationshipFields = dbFields.filter(
    (f) => f.type === 'relationship' && f.relationship && !f.multiple
  )
  const hasRelationships = relationshipFields.length > 0

  const allDbFields = [...dbFields]
  if (!dbFields.some((f) => f.name === 'createdAt')) {
    allDbFields.push({ name: 'createdAt', type: 'timestamp' as const, required: true })
  }
  if (!dbFields.some((f) => f.name === 'updatedAt')) {
    allDbFields.push({ name: 'updatedAt', type: 'timestamp' as const, required: true })
  }
  for (const field of createAuthorshipFields()) {
    if (!dbFields.some((f) => f.name === field.name)) {
      allDbFields.push(field)
    }
  }

  const upsertFields = dbFields.filter(
    (f) =>
      !f.primaryKey &&
      f.name !== 'createdAt' &&
      f.name !== 'updatedAt' &&
      !isAuthorshipFieldName(f.name)
  )

  // --- Build types ---
  const dataFields = allDbFields
    .map(
      (f) =>
        `  ${quotePropertyName(f.name)}: ${getReadFieldType(f)}${
          f.type === 'select' || f.type === 'relationship' || f.required || f.primaryKey
            ? ''
            : ' | null'
        }`
    )
    .join('\n')
  const htmlFieldTypes = htmlOutputFields.map((f) => `  ${f.name}Html: string`).join('\n')
  const m2mFieldTypes = m2mFields.map((f) => `  ${quotePropertyName(f.name)}: string[]`).join('\n')

  const hasTimestampFields =
    allDbFields.some((f) => f.name === 'createdAt') &&
    allDbFields.some((f) => f.name === 'updatedAt')
  const hasFormattedTimestampFields = allDbFields.some((f) => f.name === 'createdAtFormatted')
  const timestampFields =
    hasTimestampFields && !hasFormattedTimestampFields
      ? '\n  createdAtFormatted: string\n  updatedAtFormatted: string'
      : ''

  const upsertInterfaceFields = upsertFields
    .map((f) => `  ${quotePropertyName(f.name)}?: ${getInputFieldType(f)}`)
    .join('\n')
  const m2mUpsertInterfaceFields = m2mFields
    .map((f) => `  ${quotePropertyName(f.name)}?: string[]`)
    .join('\n')
  const allUpsertInterfaceFields = [upsertInterfaceFields, m2mUpsertInterfaceFields]
    .filter(Boolean)
    .join('\n')

  const fieldMeta = upsertFields
    .map((f) => `{ name: '${f.name}', type: '${f.type}', required: ${f.required ?? false} }`)
    .join(',\n    ')

  const cacheTag = `${schema.name}:all`
  const cacheTagsVar = `${tableVar}CacheTags`
  const nextMajor = options.nextMajorVersion ?? 16
  const cacheInvalidationFn = nextMajor >= 16 ? 'updateTag' : 'revalidateTag'
  const auditCategory = schema.name === 'settings' ? 'settings' : 'entity'
  const cacheReadImport =
    nextMajor >= 16
      ? `import { cacheLife, cacheTag } from 'next/cache'`
      : `import { unstable_cacheTag as cacheTag } from 'next/cache'`
  const cacheLifeLine = nextMajor >= 16 ? `\n  cacheLife('max')` : ''
  const singletonId = '00000000-0000-0000-0000-000000000001'
  const selectOptionsCtx: SelectOptionCtx = {
    singular,
    plural,
    Singular,
    camelSingular,
    cacheReadImport,
    cacheLifeLine,
    cacheInvalidationFn,
    creatableSelectFields,
    selectOptionsTableVar: `${tableVar}SelectOptions`
  }

  // --- types.ts ---
  const relationshipDataTypes = buildRelationshipDataTypes(allDbFields)
  const typesContent = `${[
    buildAuthorshipUserType(),
    buildReadSelectOptionType(selectFields),
    relationshipDataTypes,
    `export interface ${Singular} {\n${dataFields}${htmlFieldTypes ? `\n${htmlFieldTypes}` : ''}${m2mFieldTypes ? `\n${m2mFieldTypes}` : ''}${timestampFields}\n${buildAuthorshipDataFields()}\n}`,
    `export interface ${Singular}UpsertInput {\n${allUpsertInterfaceFields}\n}`,
    `export interface ${Singular}UpsertResult {\n  success: boolean\n  error?: string\n  ${camelSingular}?: ${Singular}\n}`,
    hasCreatableSelectFields ? buildSelectOptionTypes(selectOptionsCtx) : null,
    `export const ${cacheTagsVar} = {\n  all: '${cacheTag}'\n} as const`
  ]
    .filter(Boolean)
    .join('\n\n')}\n`

  // --- get-{singular}.ts ---
  const authorshipSelects = buildAuthorshipSelects(hasRelationships ? '        ' : '      ')
  const authorshipJoins = buildAuthorshipJoins(tableVar)
  const selectClause = hasRelationships
    ? buildRelationshipSelect(
        allDbFields,
        relationshipFields,
        tableVar,
        htmlOutputFields,
        authorshipSelects,
        authorshipJoins
      )
    : buildExplicitSelect(
        allDbFields,
        tableVar,
        htmlOutputFields,
        authorshipSelects,
        authorshipJoins
      )
  const singleRowMapping = buildSingleRowMapping(
    allDbFields,
    relationshipFields,
    `${Singular}`,
    htmlOutputFields
  )
  const m2mReadQueries = m2mFields
    .map((field) => {
      const { junctionVar, schemaIdCol, relationIdCol } = getSingleM2MContext(schema.name, field)
      return `    const ${field.name}Rows = await db
      .select({ ${relationIdCol}: ${junctionVar}.${relationIdCol} })
      .from(${junctionVar})
      .where(eq(${junctionVar}.${schemaIdCol}, SINGLETON_ID))`
    })
    .join('\n')
  const m2mAssignments = m2mFields
    .map((field) => {
      const { relationIdCol } = getSingleM2MContext(schema.name, field)
      return `    singleRow.${field.name} = ${field.name}Rows.map((relationRow) => relationRow.${relationIdCol})`
    })
    .join('\n')
  const m2mReadBlock = hasM2M ? `\n${m2mReadQueries}\n${m2mAssignments}` : ''
  const singleRowReturn = hasSelectFields
    ? `const row = result[0]\n    const singleRow = ${singleRowMapping}${m2mReadBlock}\n    const [resolvedSelectRow] = await resolve${Singular}SelectFields([singleRow])\n    return resolvedSelectRow`
    : `const row = result[0]\n    const singleRow = ${singleRowMapping}${m2mReadBlock}\n    return singleRow`
  const relationshipTableImports = relationshipFields
    .filter((field) => !isUserRelationshipTarget(field.relationship))
    .map((field) => toCamelCase(field.relationship!))
  const m2mJunctionImports = m2mFields.map(
    (field) => getSingleM2MContext(schema.name, field).junctionVar
  )
  const getDbImports = Array.from(
    new Set([
      tableVar,
      'user',
      ...relationshipTableImports,
      ...m2mJunctionImports,
      ...(hasCreatableSelectFields ? [`${tableVar}SelectOptions`] : [])
    ])
  ).sort()
  const selectResolutionFunctions = hasSelectFields
    ? `\n${generateSingleResolveSelectFieldsFunction(Singular, selectFields, `${tableVar}SelectOptions`, hasCreatableSelectFields)}\n`
    : ''

  const getContent = `'use server'

import db from '@admin/db'
import { ${getDbImports.join(', ')} } from '@admin/db/schema'
import { eq } from 'drizzle-orm'
import { alias } from 'drizzle-orm/pg-core'
${cacheReadImport}
import { ${cacheTagsVar} } from './types'
import type { ${Singular}${hasSelectFields ? ', SelectOptionValue' : ''} } from './types'

${buildAuthorshipAliasBlock()}

const SINGLETON_ID = '${singletonId}'

function formatActionDate(value: unknown): string {
  if (typeof value !== 'string') return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    timeZone: 'UTC'
  })
}
${selectResolutionFunctions}
export async function get${Singular}(): Promise<${Singular} | null> {
  'use cache'${cacheLifeLine}
  cacheTag(${cacheTagsVar}.all)

  try {
    const result = await ${selectClause}.where(eq(${tableVar}.id, SINGLETON_ID)).limit(1)
    if (result.length === 0) return null
    ${singleRowReturn}
  } catch (error) {
    console.error('Error fetching ${singular}:', error)
    throw new Error('Failed to fetch ${singular}')
  }
}
`

  // --- upsert-{singular}.ts ---
  const markdownImport = hasHtmlOutput ? `\nimport { renderMarkdownSync } from './markdown'` : ''
  const htmlUpsertBlock = hasHtmlOutput
    ? `\n` +
      htmlOutputFields
        .map(
          (f) =>
            `    if (processedData.${f.name} !== undefined) {\n      processedData.${f.name}Html = renderMarkdownSync(String(processedData.${f.name} || ''))\n    }`
        )
        .join('\n') +
      '\n'
    : ''
  const m2mInputDeclarations = m2mFields
    .map((field) => {
      const { inputIdsVar } = getSingleM2MContext(schema.name, field)
      return `    const ${inputIdsVar} = Array.isArray(input.${field.name}) ? input.${field.name} : []`
    })
    .join('\n')
  const m2mWriteStatements = m2mFields
    .map((field) => {
      const { junctionVar, schemaIdCol, relationIdCol, relationIdVar, inputIdsVar } =
        getSingleM2MContext(schema.name, field)
      return `      await tx.delete(${junctionVar}).where(eq(${junctionVar}.${schemaIdCol}, SINGLETON_ID))
      if (${inputIdsVar}.length > 0) {
        await tx.insert(${junctionVar}).values(
          ${inputIdsVar}.map((${relationIdVar}) => ({
            ${schemaIdCol}: SINGLETON_ID,
            ${relationIdCol}: ${relationIdVar}
          }))
        )
      }`
    })
    .join('\n')
  const upsertDbImports = Array.from(new Set([tableVar, ...m2mJunctionImports])).sort()
  const upsertContent = `'use server'

import { requireRole, UserRole } from '@admin/auth/middleware'
import { getAuditRequestMetadata } from '@admin/actions/audit/request-metadata'
import db from '@admin/db'
import { ${upsertDbImports.join(', ')} } from '@admin/db/schema'
import { eq } from 'drizzle-orm'
import { runAfterHooks, runBeforeHooks } from '@admin/lib/lifecycle-hooks'
import { getChangedFieldNames, getTouchedFieldNames } from '@admin/utils/audit/audit'
import { ${cacheInvalidationFn} } from 'next/cache'
import { get${Singular} } from './get-${kebabSingular}'
import { ${cacheTagsVar} } from './types'
import type { ${Singular}UpsertInput, ${Singular}UpsertResult, ${Singular} } from './types'${markdownImport}

const SINGLETON_ID = '${singletonId}'

export async function upsert${Singular}(input: ${Singular}UpsertInput): Promise<${Singular}UpsertResult> {
  const actor = await requireRole([UserRole.ADMIN, UserRole.EDITOR])

  try {
    const actorId = actor.id
    const fieldMeta = [
    ${fieldMeta}
    ]
${m2mInputDeclarations ? `${m2mInputDeclarations}\n` : ''}

    const processedData: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(input)) {
      const field = fieldMeta.find(f => f.name === key)
      if (!field) continue
      if (field.type === 'list') {
        processedData[key] = value || []
      } else if (!field.required && ['number', 'decimal'].includes(field.type)) {
        processedData[key] =
          value === undefined ||
          value === null ||
          value === '' ||
          (typeof value === 'number' && Number.isNaN(value))
            ? null
            : value
      } else if (!field.required && ['date', 'timestamp', 'time', 'string', 'email', 'varchar', 'text', 'select', 'relationship'].includes(field.type)) {
        processedData[key] = value && value !== '' ? value : null
      } else {
        processedData[key] = value
      }
    }
${htmlUpsertBlock}
    const now = new Date().toISOString()
    const auditRequestMetadata = await getAuditRequestMetadata()
    const [previousRow] = await db
      .select()
      .from(${tableVar})
      .where(eq(${tableVar}.id, SINGLETON_ID))
      .limit(1)
    const changedFields = previousRow
      ? getChangedFieldNames(previousRow as unknown as Record<string, unknown>, processedData)
      : getTouchedFieldNames(processedData)

    const lifecycleContext = {
      user: actor,
      entityType: '${schema.name}',
      schemaType: 'single' as const,
      auditCategory: '${auditCategory}' as const,
      timestamp: now,
      sourceType: 'admin_ui' as const,
      ipAddress: auditRequestMetadata.ipAddress,
      userAgent: auditRequestMetadata.userAgent,
      entityId: SINGLETON_ID,
      input: processedData,
      changedFields,
      targetLabel: ${JSON.stringify(schema.label)}
    }
    await runBeforeHooks(previousRow ? 'beforeUpdate' : 'beforeCreate', lifecycleContext)

    const result = await db.transaction(async (tx) => {
      const rows = await tx
        .insert(${tableVar})
        .values({
          id: SINGLETON_ID,
          ...processedData,
          createdAt: now,
          updatedAt: now,
          createdBy: actorId,
          updatedBy: actorId
        })
        .onConflictDoUpdate({
          target: ${tableVar}.id,
          set: { ...processedData, updatedAt: now, updatedBy: actorId }
        })
        .returning()
${m2mWriteStatements ? `\n${m2mWriteStatements}\n` : ''}
      return rows
    })

    ${cacheInvalidationFn}(${cacheTagsVar}.all)
    const saved${Singular} = await get${Singular}()
    runAfterHooks(previousRow ? 'afterUpdate' : 'afterCreate', {
      ...lifecycleContext,
      data: (saved${Singular} ?? result[0]) as unknown as Record<string, unknown>
    })

    return {
      success: true,
      ${camelSingular}: saved${Singular} ?? (result[0] as unknown as ${Singular})
    }
  } catch (error) {
    console.error('Error upserting ${singular}:', error)
    throw new Error(error instanceof Error ? error.message : 'Failed to save ${singular}')
  }
}
`

  // --- index.ts (barrel) ---
  const selectOptionTypeExports = hasCreatableSelectFields
    ? `, ${Singular}SelectOption, ${Singular}SelectOptionFieldName, Create${Singular}SelectOptionInput, Create${Singular}SelectOptionResult`
    : ''
  const selectOptionFunctionExports = hasCreatableSelectFields
    ? `
export { get${Singular}SelectOptions } from './get-${kebabSingular}-select-options'
export { create${Singular}SelectOption } from './create-${kebabSingular}-select-option'
`
    : ''
  const barrelContent = `export type { ${Singular}, ${Singular}UpsertInput, ${Singular}UpsertResult${selectOptionTypeExports} } from './types'
export { get${Singular} } from './get-${kebabSingular}'
export { upsert${Singular} } from './upsert-${kebabSingular}'${selectOptionFunctionExports}
`

  const files = [
    { name: 'types.ts', content: typesContent },
    ...(hasHtmlOutput ? [{ name: 'markdown.ts', content: genMarkdownRendererContent() }] : []),
    { name: `get-${kebabSingular}.ts`, content: getContent },
    ...(hasCreatableSelectFields
      ? [
          {
            name: `get-${kebabSingular}-select-options.ts`,
            content: genGetSelectOptionsContent(selectOptionsCtx)
          },
          {
            name: `create-${kebabSingular}-select-option.ts`,
            content: genCreateSelectOptionContent(selectOptionsCtx)
          }
        ]
      : []),
    { name: `upsert-${kebabSingular}.ts`, content: upsertContent },
    { name: 'index.ts', content: barrelContent }
  ]

  return {
    files: files.map((file) => createGeneratedFile(`${schemaActionDir}/${file.name}`, file.content))
  }
}
